import { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import ButtonEl from '../helpers/ctas/Button'

const INVOICE_GET_URL = '/umbraco/surface/invoice/get'
const INVOICE_DOWNLOAD_URL = '/umbraco/surface/invoice/download'

const ERROR_MESSAGES = {
  'not-authenticated': 'You must be logged in to view your invoice.',
  'no-pdf': 'Your invoice is not yet available. Please try again shortly.',
  default: 'Unable to load your invoice. Please try again or contact support.',
}

function resolveErrorMessage(errMessage) {
  return ERROR_MESSAGES[errMessage] ?? ERROR_MESSAGES.default
}

/**
 * Fetches and displays the member's latest invoice with a download and payment CTA.
 * Calls GET /umbraco/surface/invoice/get which returns { invoiceId, invoiceNumber, base64Pdf }.
 * The member-facing reference is the friendly invoiceNumber ("Invoice ID" in D365, e.g. INM-0016761),
 * not the invoiceId GUID.
 */
function InvoicePage({ title, description, paymentPageUrl }) {
  const [status, setStatus] = useState('loading') // loading | ready | error
  const [invoiceReference, setInvoiceReference] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    const loadInvoice = async () => {
      try {
        const res = await fetch(INVOICE_GET_URL, { credentials: 'same-origin' })

        if (res.status === 401) throw new Error('not-authenticated')
        if (!res.ok) throw new Error(`HTTP ${res.status}`)

        const data = await res.json()
        if (!data?.base64Pdf) throw new Error('no-pdf')

        // Prefer the friendly invoice number; fall back to the GUID only if it is missing.
        setInvoiceReference(data.invoiceNumber ?? data.invoiceId ?? null)
        setStatus('ready')
      } catch (err) {
        console.error('[InvoicePage] Failed to load invoice:', err)
        setErrorMessage(resolveErrorMessage(err.message))
        setStatus('error')
      }
    }

    loadInvoice()
  }, [])

  return (
    <div className="invoice-page container mx-auto px-5 py-10 xl:px-0">
      {title && <h1 className="text-midnight-500 mb-4 text-3xl font-semibold">{title}</h1>}
      {description && <p className="text-grey mb-8">{description}</p>}

      {status === 'loading' && (
        <div className="flex items-center gap-3" role="status" aria-live="polite">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-(--color-button-primary-bg)" aria-hidden="true" />
          <p className="text-grey">Loading your invoice…</p>
        </div>
      )}

      {status === 'error' && (
        <div className="invoice-page__error rounded border border-red-300 bg-red-50 p-4" role="alert">
          <p className="text-red-700">{errorMessage}</p>
        </div>
      )}

      {status === 'ready' && (
        <div className="invoice-page__content flex flex-col gap-4">
          {invoiceReference && (
            <p className="text-grey py-4">
              Invoice number: <span className="font-medium">{invoiceReference}</span>
            </p>
          )}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            {/* Real <a> (not a click-handler button) with target+download so the PDF download
                escapes the sandboxed Umbraco preview iframe. */}
            <ButtonEl
              link={{ url: INVOICE_DOWNLOAD_URL, target: '_blank', rel: 'noopener', download: true }}
              theme="secondary"
              icon="ArrowDownTray"
              position="After"
            >
              Download invoice
            </ButtonEl>
            {paymentPageUrl && (
              <ButtonEl link={{ url: paymentPageUrl, name: 'Proceed to Payment' }} theme="primary" />
            )}
          </div>
        </div>
      )}
    </div>
  )
}

InvoicePage.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  paymentPageUrl: PropTypes.string,
}

export default InvoicePage
